<template>
	<div
		class="cms-navigation-block"
		:class="rootClasses"
		:style="rootStyle"
	>
		<!-- Mobile top bar: fixed to the screen when "Show mobile bar" is on,
			otherwise rendered in place (for a second menu lower on the page). -->
		<div
			v-if="isMobileLayout"
			class="cms-nav__mobile-bar"
			:class="{ 'cms-nav__mobile-bar--inline': !isOn(showMobileBar) }"
			:style="mobileBarComputedStyle"
		>
			<a :href="logoUrl || '/'" class="cms-nav__mobile-logo" :style="mobileLogoStyle">
				<img v-if="resolvedLogoSrc" :src="resolvedLogoSrc" :alt="siteName" :style="logoImgStyle">
				<span v-else class="cms-nav__mobile-sitename" :style="mobileSitenameStyle">{{ siteName || 'Home' }}</span>
			</a>
			<div class="cms-nav__mobile-actions">
				<button
					v-if="isOn(showSearch)"
					type="button"
					class="cms-nav__mobile-toggle"
					:style="mobileToggleStyle"
					:aria-label="t('navigation.search')"
					data-testid="cms-nav-search-open"
					@click="openSearch"
				>
					<i class="fa fa-search"></i>
				</button>
				<button
					type="button"
					class="cms-nav__mobile-toggle"
					:style="mobileToggleStyle"
					:aria-expanded="mobileOpen ? 'true' : 'false'"
					:aria-label="t('navigation.toggleMenu')"
					@click="toggleMobile"
				>
					<i :class="['fa', mobileOpen ? 'fa-times' : (mobileToggleIcon || 'fa-bars')]"></i>
				</button>
			</div>
		</div>
		<!-- Keeps the fixed mobile bar's height in the page flow -->
		<div v-if="isMobileLayout && isOn(showMobileBar)" class="cms-nav__mobile-spacer" :style="{ height: mobileBarHeight }"></div>

		<!-- Desktop horizontal menu -->
		<nav
			v-if="!isMobileLayout"
			class="cms-nav__desktop"
			:class="desktopNavClasses"
			:style="desktopNavComputedStyle"
		>
			<!-- Logo (desktop) -->
			<a v-if="showDesktopLogo" :href="logoUrl || '/'" class="cms-nav__desktop-logo" :style="desktopLogoStyle">
				<img v-if="resolvedLogoSrc" :src="resolvedLogoSrc" :alt="siteName" :style="logoImgStyle">
				<span v-else class="cms-nav__desktop-sitename" :style="desktopSitenameStyle">{{ siteName || 'Home' }}</span>
			</a>

			<ul class="cms-nav__list cms-nav__list--root" :style="rootListStyle">
				<li
					v-for="item in topLevelItems"
					:key="item.id"
					class="cms-nav__item"
					:class="[
						'cms-nav__item--' + item.type,
						{
							'cms-nav__item--has-children': opensPanel(item),
							'cms-nav__item--mega': isMegaItem(item),
							'cms-nav__item--active': isActiveItem(item)
						}
					]"
					@mouseenter="hoverEnter(item.id)"
					@mouseleave="hoverLeave(item.id)"
				>
					<a
						:href="safeUrl(item.url)"
						:target="item.target || '_self'"
						:class="['cms-nav__link', { 'cms-nav__link--has-children': opensPanel(item), 'cms-nav__link--active': isActiveItem(item) }, item.cssClass]"
						:style="computedLinkStyle(item)"
						@click="onLinkClick(item, $event)"
					>
						<i v-if="item.icon" :class="['fa', item.icon, 'cms-nav__icon']"></i>
						<span class="cms-nav__label">{{ item.label }}</span>
						<span
							v-if="item.badgeText"
							class="cms-nav__badge"
							:style="badgeStyle(item)"
						>{{ item.badgeText }}</span>
						<i
							v-if="opensPanel(item)"
							:class="['fa', caretIcon, 'cms-nav__caret']"
							:style="caretComputedStyle"
						></i>
					</a>

					<!-- Mega menu panel -->
					<div
						v-if="isMegaItem(item) && isOpen(item.id)"
						class="cms-nav__mega"
						:class="['cms-nav__mega--' + (item.megaLayout || megaDefaultCols), { 'cms-nav__mega--page': megaSpan === 'page' }]"
						:style="megaPanelComputedStyle"
					>
						<!-- Built mega (menu manager): columns of blocks -->
						<div v-if="megaOf(item)" class="cms-nav__mega-grid cms-nav__mega-grid--built" :style="{ gridTemplateColumns: megaGrid(item) }">
							<div v-for="(col, ci) in megaOf(item).columns" :key="'c' + ci" class="cms-nav__mega-col">
								<template v-for="(blk, bi) in col.blocks">
									<div v-if="blk.type === 'links'" :key="'b' + bi" class="cms-nav__mega-block cms-nav__mega-block--links">
										<div v-if="blk.heading" class="cms-nav__mega-heading" :style="megaHeadingComputedStyle">{{ blk.heading }}</div>
										<ul class="cms-nav__mega-list">
											<li v-for="child in megaLinksFor(item, ci, bi)" :key="child.id">
												<a :href="safeUrl(child.url)" :target="child.target || '_self'" class="cms-nav__mega-link" :style="megaLinkComputedStyle" @click="onLinkClick(child, $event)">
													<i v-if="child.icon" :class="['fa', child.icon]"></i>
													<span>{{ child.label }}</span>
													<span v-if="child.description" class="cms-nav__mega-link-desc">{{ child.description }}</span>
												</a>
											</li>
										</ul>
									</div>

									<div v-else-if="blk.type === 'stories'" :key="'b' + bi" class="cms-nav__mega-block">
										<div v-if="blk.heading" class="cms-nav__mega-heading" :style="megaHeadingComputedStyle">{{ blk.heading }}</div>
										<div class="cms-nav__mega-posts" :class="'cms-nav__mega-posts--' + (blk.layout || 'cards')">
											<a v-for="p in (blk.posts || [])" :key="p.websitePostID" :href="safeUrl(p.websitePostUrl)" class="cms-nav__mega-post" :style="megaLinkComputedStyle">
												<span v-if="(blk.layout || 'cards') === 'cards' && p.websitePostFeatureImageUrl" class="cms-nav__mega-post-img" :style="{ backgroundImage: 'url(\'' + p.websitePostFeatureImageUrl + '\')' }"></span>
												<span class="cms-nav__mega-post-title">{{ p.websitePostTitle }}</span>
											</a>
											<span v-if="!(blk.posts && blk.posts.length)" class="cms-nav__mega-desc" :style="megaDescComputedStyle">No stories yet</span>
										</div>
									</div>

									<component :is="blk.url ? 'a' : 'div'" v-else-if="blk.type === 'image'" :key="'b' + bi" :href="blk.url ? safeUrl(blk.url) : null" class="cms-nav__mega-block cms-nav__mega-image">
										<span class="cms-nav__mega-promo-img" :style="blk.src ? { backgroundImage: bgImage(blk.src, 640) } : null"></span>
										<span v-if="blk.caption" class="cms-nav__mega-desc" :style="megaDescComputedStyle">{{ blk.caption }}</span>
									</component>

									<div v-else-if="blk.type === 'text'" :key="'b' + bi" class="cms-nav__mega-block">
										<div v-if="blk.heading" class="cms-nav__mega-heading" :style="megaHeadingComputedStyle">{{ blk.heading }}</div>
										<div v-if="blk.html" class="cms-nav__mega-desc cms-nav__mega-text" :style="megaDescComputedStyle" v-html="blk.html"></div>
									</div>

									<div v-else-if="blk.type === 'button'" :key="'b' + bi" class="cms-nav__mega-block">
										<a :href="safeUrl(blk.url || '#')" class="cms-nav__mega-promo-btn" :class="{ 'cms-nav__mega-promo-btn--outline': blk.style === 'outline' }">{{ blk.label || 'Learn more' }}</a>
									</div>

									<hr v-else-if="blk.type === 'divider'" :key="'b' + bi" class="cms-nav__mega-divider">
								</template>
							</div>
						</div>

						<!-- Legacy mega: each child is a column of grandchildren -->
						<div v-else class="cms-nav__mega-grid">
							<div
								v-for="child in item.children"
								:key="child.id"
								class="cms-nav__mega-col"
							>
								<div class="cms-nav__mega-heading" :style="megaHeadingComputedStyle">{{ child.label }}</div>
								<div v-if="child.description" class="cms-nav__mega-desc" :style="megaDescComputedStyle">{{ child.description }}</div>
								<ul v-if="hasChildren(child)" class="cms-nav__mega-list">
									<li v-for="grandchild in child.children" :key="grandchild.id">
										<a
											:href="safeUrl(grandchild.url)"
											:target="grandchild.target || '_self'"
											class="cms-nav__mega-link"
											:style="megaLinkComputedStyle"
										>
											<i v-if="grandchild.icon" :class="['fa', grandchild.icon]"></i>
											<span>{{ grandchild.label }}</span>
											<span v-if="grandchild.description" class="cms-nav__mega-link-desc">{{ grandchild.description }}</span>
										</a>
									</li>
								</ul>
							</div>
						</div>
					</div>

					<!-- Standard dropdown -->
					<ul
						v-else-if="hasChildren(item) && isOpen(item.id)"
						class="cms-nav__list cms-nav__list--dropdown"
						:style="dropdownComputedStyle"
					>
						<li
							v-for="child in item.children"
							:key="child.id"
							class="cms-nav__item cms-nav__item--child"
							:class="{ 'cms-nav__item--has-children': hasChildren(child) }"
						>
							<a
								:href="safeUrl(child.url)"
								:target="child.target || '_self'"
								class="cms-nav__link cms-nav__link--child"
								:style="dropdownLinkComputedStyle"
							>
								<i v-if="child.icon" :class="['fa', child.icon]"></i>
								<span>{{ child.label }}</span>
								<i v-if="hasChildren(child)" class="fa fa-chevron-right cms-nav__caret--child"></i>
							</a>
							<ul v-if="hasChildren(child)" class="cms-nav__list cms-nav__list--submenu" :style="dropdownComputedStyle">
								<li
									v-for="grandchild in child.children"
									:key="grandchild.id"
									class="cms-nav__item cms-nav__item--child"
								>
									<a
										:href="safeUrl(grandchild.url)"
										:target="grandchild.target || '_self'"
										class="cms-nav__link cms-nav__link--child"
										:style="dropdownLinkComputedStyle"
									>
										<i v-if="grandchild.icon" :class="['fa', grandchild.icon]"></i>
										<span>{{ grandchild.label }}</span>
									</a>
								</li>
							</ul>
						</li>
					</ul>
				</li>
			</ul>

			<!-- Search (desktop) -->
			<button
				v-if="isOn(showSearch)"
				type="button"
				class="cms-nav__search-btn"
				:style="searchBtnStyle"
				:aria-label="t('navigation.search')"
				:title="t('navigation.search')"
				data-testid="cms-nav-search-open"
				@click="openSearch"
			>
				<i class="fa fa-search"></i>
			</button>

			<!-- CTA Button (desktop) -->
			<a
				v-if="ctaText"
				:href="ctaUrl || '#'"
				:target="ctaTarget || '_self'"
				class="cms-nav__cta"
				:style="ctaComputedStyle"
			>{{ ctaText }}</a>
		</nav>

		<!-- Mobile overlay drawer -->
		<transition name="cms-nav-overlay-fade">
			<div
				v-if="isMobileLayout && mobileOpen"
				class="cms-nav__overlay"
				:style="overlayBgStyle"
				@click.self="closeMobile"
			>
				<div class="cms-nav__overlay-panel" :style="overlayPanelComputedStyle">
					<div class="cms-nav__overlay-header" :style="overlayHeaderStyle">
						<a :href="logoUrl || '/'" class="cms-nav__mobile-logo" :style="mobileLogoStyle">
							<img v-if="resolvedLogoSrc" :src="resolvedLogoSrc" :alt="siteName" :style="logoImgStyle">
							<span v-else class="cms-nav__mobile-sitename" :style="mobileSitenameStyle">{{ siteName || 'Home' }}</span>
						</a>
						<button
							type="button"
							class="cms-nav__mobile-toggle"
							:style="mobileToggleStyle"
							:aria-label="t('navigation.closeMenu')"
							@click="closeMobile"
						>
							<i class="fa fa-times"></i>
						</button>
					</div>
					<ul class="cms-nav__list cms-nav__list--mobile" :style="mobileListStyle">
						<li
							v-for="item in mobileMenuItems"
							:key="'m-' + item.id"
							class="cms-nav__mobile-item"
							:style="mobileItemStyle"
						>
							<div class="cms-nav__mobile-row">
								<a
									:href="safeUrl(item.url)"
									:target="item.target || '_self'"
									class="cms-nav__mobile-link"
									:style="mobileLinkComputedStyle"
									@click="closeMobile"
								>
									<i v-if="item.icon" :class="['fa', item.icon]"></i>
									<span>{{ item.label }}</span>
								</a>
								<button
									v-if="hasChildren(item)"
									type="button"
									class="cms-nav__mobile-expand"
									:style="mobileExpandBtnStyle"
									:aria-expanded="mobileExpanded[item.id] ? 'true' : 'false'"
									@click="toggleMobileItem(item.id)"
								>
									<i :class="['fa', mobileExpanded[item.id] ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
								</button>
							</div>
							<ul
								v-if="hasChildren(item) && mobileExpanded[item.id]"
								class="cms-nav__list cms-nav__list--mobile-children"
								:style="mobileChildListStyle"
							>
								<li
									v-for="child in flattenChildren(item)"
									:key="'mc-' + child.id"
									class="cms-nav__mobile-item cms-nav__mobile-item--child"
								>
									<a
										:href="safeUrl(child.url)"
										:target="child.target || '_self'"
										:class="['cms-nav__mobile-link', 'cms-nav__mobile-link--child']"
										:style="mobileChildLinkStyle(child)"
										@click="closeMobile"
									>
										<i v-if="child.icon" :class="['fa', child.icon]"></i>
										<span>{{ child.label }}</span>
									</a>
								</li>
							</ul>
						</li>
					</ul>
					<!-- Mobile CTA -->
					<div v-if="ctaText" class="cms-nav__mobile-cta-wrap">
						<a
							:href="ctaUrl || '#'"
							:target="ctaTarget || '_self'"
							class="cms-nav__mobile-cta"
							:style="mobileCTAStyle"
							@click="closeMobile"
						>{{ ctaText }}</a>
					</div>
				</div>
			</div>
		</transition>

		<!-- Search modal: one field, submits to the site search page -->
		<transition name="cms-nav-overlay-fade">
			<div v-if="searchOpen" class="cms-nav__search-modal" role="dialog" aria-modal="true" :aria-label="t('navigation.search')" @click.self="closeSearch" @keydown.esc="closeSearch">
				<form class="cms-nav__search-form" @submit.prevent="submitSearch">
					<i class="fa fa-search cms-nav__search-icon"></i>
					<input
						ref="searchInput"
						v-model="searchQuery"
						type="text"
						class="cms-nav__search-input"
						:placeholder="searchPlaceholder || t('navigation.searchPlaceholder')"
						autocomplete="off"
						data-testid="cms-nav-search-input"
					/>
					<button type="submit" class="cms-nav__search-submit" :style="ctaComputedStyle">{{ t('navigation.search') }}</button>
					<button type="button" class="cms-nav__search-close" :aria-label="t('navigation.closeSearch')" @click="closeSearch"><i class="fa fa-times"></i></button>
				</form>
			</div>
		</transition>

		<!-- Empty state (editor only) -->
		<div v-if="cmsEditorContext && !resolvedItems.length && !hasMenuSelected" class="cms-nav__empty">
			<i class="fa fa-bars"></i>
			<span>Select a menu in the sidebar to display navigation.</span>
		</div>
		<div v-else-if="cmsEditorContext && !resolvedItems.length" class="cms-nav__empty">
			<i class="fa fa-bars"></i>
			<span>This menu has no items yet.</span>
		</div>
	</div>
</template>

<script>
/**
 * NavigationBlock.vue
 * ───────────────────
 * A highly-customisable navigation block with desktop dropdowns / mega-menus,
 * a mobile-friendly overlay drawer, optional CTA button, and full styling
 * control for every visual element.
 *
 * Uses shared typography/background models and prop factories to stay DRY.
 *
 * Menu data comes from one of:
 *   1. inline `items` prop — used for editor preview
 *   2. globally injected `siteNavigations.bySlug[useNavigationSlug]`
 *   3. globally injected `siteNavigations.byLocation[useSiteLocation]`
 */

import { createTypographyProps } from '@/websites/cms/blocks/shared/typographyProps.js';
import { t } from '@/websites/i18n/websiteI18n';
import { normaliseMega, gridTemplate, assignLinks } from './megaMenuModel.js';
import { bgImage } from '@/websites/cms/blocks/shared/imageSrc.js';
import { createBackgroundProps } from '@/websites/cms/blocks/shared/backgroundProps.js';
import { buildTypographyStyle, buildBackgroundStyle, readableTextOn } from '@/websites/cms/blocks/shared/blockUtils.js';
import TypographyModel from '@/websites/cms/models/TypographyModel.js';
import BackgroundModel from '@/websites/cms/models/BackgroundModel.js';

var DEFAULT_BREAKPOINT = 768;

export default {
	name: 'NavigationBlock',

	inject: {
		siteNavigations: { default: function () { return { bySlug: {}, byLocation: {} }; } },
		siteSettings:    { default: function () { return {}; } },
		cmsEditorContext: { default: false },
		// Editor canvas provides { width } so the block can follow the
		// phone frame instead of the real window width.
		cmsViewport: { default: null }
	},

	props: Object.assign(
		{},
		// Menu-item font and the nav bar background are style traits
		// (Typography / Background) applied by the block wrapper.
		// ── Typography: dropdown items ──
		createTypographyProps('dropdown', { fontSize: '0.925em', fontWeight: '400' }),
		// ── Typography: mega headings ──
		createTypographyProps('megaHeading', { fontSize: '0.85em', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em' }),
		// ── Background: dropdown/mega panels ──
		createBackgroundProps('dropdown', { backgroundColor: '#FFFFFF' }),
		{
			// ── Menu source ──
			navigationId:      { type: [Number, String], default: null },
			useNavigationSlug: { type: String, default: '' },
			useSiteLocation:   { type: String, default: '' },
			items:             { type: Array,  default: function () { return []; } },

			// ── Layout ──
			layout:            { type: String, default: 'horizontal' },
			alignment:         { type: String, default: 'left' },
			verticalAlignment: { type: String, default: 'center' },
			mobileBreakpoint:  { type: [Number, String], default: DEFAULT_BREAKPOINT },
			mobileToggleIcon:  { type: String, default: 'fa-bars' },
			showMobileBar:     { type: [Boolean, String], default: true },
			// ── Search ──
			showSearch:        { type: [Boolean, String], default: false },
			searchUrl:         { type: String, default: '/search' },
			searchPlaceholder: { type: String, default: '' }, // blank = translated default
			showDesktopLogo:   { type: [Boolean, String], default: false },
			stickyNav:         { type: [Boolean, String], default: false },
			/** Thin-bar rendering inside the condensed sticky header: tighter spacing, no min height, inherits the bar colors. */
			condensed:         { type: [Boolean, String], default: false },

			// ── Spacing & Sizing ──
			itemSpacing:       { type: String, default: '1.5rem' },
			itemPadding:       { type: String, default: '0.5rem 0.75rem' },
			navHeight:         { type: String, default: '' },
			navMaxWidth:       { type: String, default: '' },
			logoMaxHeight:     { type: String, default: '40px' },
			logoMaxWidth:      { type: String, default: '160px' },

			// ── Item Colors ──
			itemColor:         { type: String, default: '' },
			/** Search icon colour; empty follows the items (itemColor, else the bar's text colour). */
			searchColor:       { type: String, default: '' },
			itemHoverColor:    { type: String, default: '' },
			itemHoverBg:       { type: String, default: '' },
			activeColor:       { type: String, default: '' },
			activeBg:          { type: String, default: '' },
			activeIndicator:   { type: String, default: 'none' },

			// ── Item border/decoration ──
			itemBorderRadius:  { type: String, default: '4px' },
			itemTransition:    { type: String, default: '0.15s' },

			// ── Dropdown styling ──
			dropdownWidth:     { type: String, default: '240px' },
			dropdownPadding:   { type: String, default: '0.4rem' },
			dropdownRadius:    { type: String, default: '10px' },
			dropdownShadow:    { type: String, default: '0 8px 32px rgba(0,0,0,0.12)' },
			dropdownBorder:    { type: String, default: '' },
			dropdownItemPadding: { type: String, default: '0.55rem 0.75rem' },
			dropdownItemHoverBg: { type: String, default: 'rgba(0,0,0,0.04)' },
			dropdownOffset:    { type: String, default: '4px' },
			dropdownAnimation: { type: String, default: 'fade' },

			// ── Mega menu ──
			megaDefaultCols:   { type: String, default: '3col' },
			megaPadding:       { type: String, default: '1.5rem' },
			megaRadius:        { type: String, default: '8px' },
			megaWidth:         { type: String, default: '100%' },
			/** 'bar' spans the navigation bar; 'page' caps the panel at the site page width and centres it. */
			megaSpan:          { type: String, default: 'bar' },
			megaGap:           { type: String, default: '1.5rem' },
			megaLinkPadding:   { type: String, default: '0.4rem 0.5rem' },
			megaLinkHoverBg:   { type: String, default: 'rgba(0,0,0,0.04)' },
			megaLinkRadius:    { type: String, default: '4px' },
			megaDescColor:     { type: String, default: '' },

			// ── Caret ──
			caretIcon:         { type: String, default: 'fa-chevron-down' },
			caretSize:         { type: String, default: '0.65em' },
			caretColor:        { type: String, default: '' },
			caretSpacing:      { type: String, default: '0.3rem' },

			// ── Badge ──
			badgeBg:           { type: String, default: '' },
			badgeColor:        { type: String, default: '#FFFFFF' },
			badgeRadius:       { type: String, default: '10px' },
			badgeFontSize:     { type: String, default: '0.7em' },

			// ── Mobile bar ──
			mobileBarBg:       { type: String, default: '#FFFFFF' },
			mobileBarColor:    { type: String, default: '#1E2031' },
			mobileBarHeight:   { type: String, default: '56px' },
			mobileBarShadow:   { type: String, default: '0 2px 8px rgba(0,0,0,0.08)' },
			mobileBarPadding:  { type: String, default: '0 14px' },
			mobileBarBorder:   { type: String, default: '' },

			// ── Mobile overlay / drawer ──
			overlayBg:         { type: String, default: 'rgba(30,32,49,0.55)' },
			overlayPanelBg:    { type: String, default: '#FFFFFF' },
			overlayPanelColor: { type: String, default: '#1E2031' },
			overlayPanelWidth: { type: String, default: '100%' },
			overlayPanelMaxWidth: { type: String, default: '420px' },
			overlayPanelShadow: { type: String, default: '-4px 0 24px rgba(0,0,0,0.2)' },
			overlayPosition:   { type: String, default: 'right' },
			mobileLinkPadding: { type: String, default: '0.85rem 14px' },
			mobileLinkFontSize: { type: String, default: '1.05em' },
			mobileLinkHoverBg: { type: String, default: 'rgba(0,0,0,0.04)' },
			mobileChildIndent: { type: String, default: '14px' },
			mobileChildBg:     { type: String, default: 'rgba(0,0,0,0.025)' },
			mobileChildColor:  { type: String, default: '' },
			mobileSeparator:   { type: [Boolean, String], default: false },
			mobileSeparatorColor: { type: String, default: 'rgba(0,0,0,0.06)' },

			// ── CTA Button ──
			ctaText:           { type: String, default: '' },
			ctaUrl:            { type: String, default: '' },
			ctaTarget:         { type: String, default: '_self' },
			ctaBg:             { type: String, default: 'var(--site-btn-primary-bg, #FB6A00)' },
			ctaColor:          { type: String, default: 'var(--site-btn-primary-color, #FFFFFF)' },
			ctaBorderColor:    { type: String, default: '' },
			ctaBorderRadius:   { type: String, default: 'var(--site-btn-radius, 4px)' },
			ctaPadding:        { type: String, default: 'var(--site-btn-padding, 0.5rem 1.25rem)' },
			ctaFontSize:       { type: String, default: 'var(--site-btn-font-size, 0.875em)' },
			ctaFontWeight:     { type: String, default: 'var(--site-btn-font-weight, 600)' },
			ctaHoverBg:        { type: String, default: '' },
			ctaHoverColor:     { type: String, default: '' },

			// ── Logo ──
			logoSrc:           { type: String, default: '' },
			logoUrl:           { type: String, default: '/' }
		}
	),

	data: function () {
		return {
			openItemId: null,
			mobileOpen: false,
			mobileExpanded: {},
			searchOpen: false,
			searchQuery: '',
			windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1200
		};
	},

	computed: {
		// ── Data resolution ──
		resolvedItems: function () {
			if (Array.isArray(this.items) && this.items.length) { return this.items; }
			var src = this.siteNavigations || {};
			if (this.useNavigationSlug && src.bySlug && src.bySlug[this.useNavigationSlug]) {
				return src.bySlug[this.useNavigationSlug].items || [];
			}
			if (this.useSiteLocation && src.byLocation && src.byLocation[this.useSiteLocation]) {
				return src.byLocation[this.useSiteLocation].items || [];
			}
			return [];
		},

		topLevelItems: function () {
			return this.resolvedItems.filter(function (i) { return i && i.type !== 'divider'; });
		},

		/** True when the block points at a menu by id, slug, or site location. */
		hasMenuSelected: function () {
			return !!(this.navigationId || this.useNavigationSlug || this.useSiteLocation);
		},

		mobileMenuItems: function () {
			var src = this.siteNavigations || {};
			if (src.byLocation && src.byLocation.mobile && (!this.useSiteLocation || this.useSiteLocation === 'primary')) {
				return src.byLocation.mobile.items || this.topLevelItems;
			}
			return this.topLevelItems;
		},

		isMobileLayout: function () {
			var bp = parseInt(this.mobileBreakpoint, 10) || DEFAULT_BREAKPOINT;
			var width = (this.cmsViewport && this.cmsViewport.width) ? this.cmsViewport.width : this.windowWidth;
			return width < bp;
		},

		siteName: function () {
			return (this.siteSettings && this.siteSettings.siteName) || '';
		},

		resolvedLogoSrc: function () {
			if (this.logoSrc) { return this.logoSrc; }
			if (this.siteSettings && this.siteSettings.logoMediaID) {
				return '/general/getMediaFile.php?mediaID=' + this.siteSettings.logoMediaID;
			}
			return '';
		},

		// ── Typography models (DRY via shared model) ──
		dropdownTypography: function () {
			return TypographyModel.fromProps(this.$props, 'dropdown');
		},

		megaHeadingTypography: function () {
			return TypographyModel.fromProps(this.$props, 'megaHeading');
		},

		// ── Background models (DRY via shared model) ──
		dropdownBackground: function () {
			return BackgroundModel.fromProps(this.$props, 'dropdown');
		},

		// ── Root container ──
		rootClasses: function () {
			var classes = [
				'cms-nav--' + this.layout,
				'cms-nav--align-' + this.alignment
			];
			if (this.isCondensed) {
				classes.push('cms-nav--condensed');
			} else if (this.stickyNav === true || this.stickyNav === 'true') {
				classes.push('cms-nav--sticky');
			}
			if (this.openItemId !== null && this.openItemId !== undefined) {
				classes.push('cms-nav--menu-open');
			}
			if (this.activeIndicator && this.activeIndicator !== 'none') {
				classes.push('cms-nav--indicator-' + this.activeIndicator);
			}
			return classes;
		},

		isCondensed: function () { return this.condensed === true || this.condensed === 'true'; },

		rootStyle: function () {
			var style = {};
			if (this.navHeight && !this.isCondensed) { style.minHeight = this.navHeight; }
			style.position = 'relative';
			return style;
		},

		// ── Desktop nav ──
		desktopNavClasses: function () {
			return [
				'cms-nav__desktop--' + this.alignment,
				'cms-nav__desktop--v-' + this.verticalAlignment
			];
		},

		desktopNavComputedStyle: function () {
			var style = {};
			if (this.navMaxWidth) { style.maxWidth = this.navMaxWidth; style.margin = '0 auto'; }
			return style;
		},

		rootListStyle: function () {
			return { gap: this.itemSpacing };
		},

		// ── Link base style ──
		linkBaseStyle: function () {
			return {
				color: this.isCondensed ? 'inherit' : (this.itemColor || ''),
				padding: this.isCondensed ? '0.35rem 0.6rem' : this.itemPadding,
				borderRadius: this.itemBorderRadius,
				transition: 'color ' + this.itemTransition + ', background ' + this.itemTransition,
				'--cms-nav-hover-color': this.itemHoverColor || '',
				'--cms-nav-hover-bg': this.itemHoverBg || '',
				'--cms-nav-active-color': this.activeColor || '',
				'--cms-nav-active-bg': this.activeBg || ''
			};
		},

		/** The search button follows the items unless it has its own colour. */
		searchBtnStyle: function () {
			var style = Object.assign({}, this.linkBaseStyle);
			if (this.searchColor) style.color = this.searchColor;
			return style;
		},

		// ── Dropdown ──
		dropdownComputedStyle: function () {
			var bgStyle = buildBackgroundStyle(this.dropdownBackground);
			bgStyle.minWidth = this.dropdownWidth;
			bgStyle.padding = this.dropdownPadding;
			bgStyle.borderRadius = this.dropdownRadius;
			bgStyle.boxShadow = this.dropdownShadow;
			// a visible surface even when the saved color is empty
			if (!bgStyle.backgroundColor && !bgStyle.backgroundImage) bgStyle.backgroundColor = '#FFFFFF';
			bgStyle.border = this.dropdownBorder || '1px solid rgba(0,0,0,0.06)';
			bgStyle.marginTop = this.dropdownOffset;
			bgStyle['--nav-drop-offset'] = this.dropdownOffset;
			return bgStyle;
		},

		dropdownLinkComputedStyle: function () {
			var typo = buildTypographyStyle(TypographyModel.toCssObject(this.dropdownTypography));
			typo.padding = this.dropdownItemPadding;
			typo['--cms-nav-dd-hover-bg'] = this.dropdownItemHoverBg;
			// The panel does not inherit the bar's text color: a white bar on a dark header
			// would otherwise show white links on the white panel. Pick a readable color
			// for the panel background unless the dropdown typography sets one.
			if (!typo.color) typo.color = readableTextOn(this.dropdownComputedStyle.backgroundColor) || 'var(--site-body-color, #1E2031)';
			return typo;
		},

		// ── Mega menu ──
		megaPanelComputedStyle: function () {
			var bgStyle = buildBackgroundStyle(this.dropdownBackground);
			bgStyle.padding = this.megaPadding;
			bgStyle.borderRadius = this.megaRadius;
			bgStyle.boxShadow = this.dropdownShadow;
			bgStyle.width = this.megaSpan === 'page' ? 'min(100%, var(--site-page-width, 1000px))' : this.megaWidth;
			bgStyle['--cms-mega-gap'] = this.megaGap;
			// same rule as the dropdown: the panel sets its own readable text color
			if (!bgStyle.backgroundColor && !bgStyle.backgroundImage) bgStyle.backgroundColor = '#FFFFFF';
			bgStyle.color = readableTextOn(bgStyle.backgroundColor) || 'var(--site-body-color, #1E2031)';
			return bgStyle;
		},

		megaHeadingComputedStyle: function () {
			return buildTypographyStyle(TypographyModel.toCssObject(this.megaHeadingTypography));
		},

		megaDescComputedStyle: function () {
			return { color: this.megaDescColor || 'rgba(30,32,49,0.6)' };
		},

		megaLinkComputedStyle: function () {
			return {
				padding: this.megaLinkPadding,
				borderRadius: this.megaLinkRadius,
				'--cms-mega-link-hover-bg': this.megaLinkHoverBg
			};
		},

		// ── Caret ──
		caretComputedStyle: function () {
			return {
				fontSize: this.caretSize,
				marginLeft: this.caretSpacing,
				color: this.caretColor || 'inherit',
				opacity: this.caretColor ? '1' : '0.6'
			};
		},

		// ── CTA button ──
		ctaComputedStyle: function () {
			return {
				backgroundColor: this.ctaBg,
				color: this.ctaColor,
				border: this.ctaBorderColor ? ('1px solid ' + this.ctaBorderColor) : 'none',
				borderRadius: this.ctaBorderRadius,
				padding: this.ctaPadding,
				fontSize: this.ctaFontSize,
				fontWeight: this.ctaFontWeight,
				'--cms-nav-cta-hover-bg': this.ctaHoverBg || '',
				'--cms-nav-cta-hover-color': this.ctaHoverColor || ''
			};
		},

		mobileCTAStyle: function () {
			return {
				backgroundColor: this.ctaBg,
				color: this.ctaColor,
				border: this.ctaBorderColor ? ('1px solid ' + this.ctaBorderColor) : 'none',
				borderRadius: this.ctaBorderRadius,
				padding: this.ctaPadding,
				fontSize: this.ctaFontSize,
				fontWeight: this.ctaFontWeight
			};
		},

		// ── Logo ──
		logoImgStyle: function () {
			return { maxHeight: this.logoMaxHeight, maxWidth: this.logoMaxWidth };
		},

		desktopLogoStyle: function () {
			return { marginRight: this.itemSpacing };
		},

		desktopSitenameStyle: function () {
			return { fontWeight: '700', fontSize: '1.1em' };
		},

		mobileLogoStyle: function () {
			return {};
		},

		mobileSitenameStyle: function () {
			return { fontWeight: '700', fontSize: '1.05em' };
		},

		// ── Mobile bar ──
		mobileBarComputedStyle: function () {
			return {
				backgroundColor: this.mobileBarBg,
				color: this.mobileBarColor,
				height: this.mobileBarHeight,
				boxShadow: this.mobileBarShadow,
				padding: this.mobileBarPadding,
				borderBottom: this.mobileBarBorder || 'none'
			};
		},

		mobileToggleStyle: function () {
			return { color: 'inherit' };
		},

		// ── Mobile overlay ──
		overlayBgStyle: function () {
			return { backgroundColor: this.overlayBg };
		},

		overlayPanelComputedStyle: function () {
			var style = {
				backgroundColor: this.overlayPanelBg,
				color: this.overlayPanelColor,
				width: this.overlayPanelWidth,
				maxWidth: this.overlayPanelMaxWidth,
				boxShadow: this.overlayPanelShadow
			};
			if (this.overlayPosition === 'left') {
				style.marginLeft = '0';
				style.marginRight = 'auto';
			} else {
				style.marginLeft = 'auto';
				style.marginRight = '0';
			}
			return style;
		},

		overlayHeaderStyle: function () {
			return { borderBottom: '1px solid ' + this.mobileSeparatorColor };
		},

		mobileListStyle: function () {
			return {};
		},

		mobileItemStyle: function () {
			if (this.mobileSeparator === true || this.mobileSeparator === 'true') {
				return { borderBottom: '1px solid ' + this.mobileSeparatorColor };
			}
			return {};
		},

		mobileLinkComputedStyle: function () {
			return {
				padding: this.mobileLinkPadding,
				fontSize: this.mobileLinkFontSize,
				color: 'inherit',
				'--cms-nav-mobile-hover-bg': this.mobileLinkHoverBg
			};
		},

		mobileExpandBtnStyle: function () {
			return { color: 'inherit' };
		},

		mobileChildListStyle: function () {
			return { backgroundColor: this.mobileChildBg };
		}
	},

	mounted: function () {
		this._onResize = this.onResize.bind(this);
		window.addEventListener('resize', this._onResize);
	},

	beforeDestroy: function () {
		clearTimeout(this._hoverCloseTimer);
		if (this._onResize) {
			window.removeEventListener('resize', this._onResize);
		}
		document.body.style.overflow = '';
	},

	methods: {
		t,
		isOn: function (v) { return v === true || v === 'true' || v === 1 || v === '1'; },

		onResize: function () {
			this.windowWidth = window.innerWidth;
		},

		hasChildren: function (item) {
			return item && Array.isArray(item.children) && item.children.length > 0;
		},

		safeUrl: function (url) {
			return url || '#';
		},

		isOpen: function (id) {
			return this.openItemId === id;
		},

		isActiveItem: function (item) {
			if (!item || !item.url || item.url === '#') return false;
			if (typeof window === 'undefined') return false;
			return window.location.pathname === item.url;
		},

		/** The item shows a caret and opens something on hover/click: a dropdown (children) or a built mega panel. */
		opensPanel: function (item) { return this.hasChildren(item) || !!this.megaOf(item); },

		/** A mega panel opens when the item has a mega layout and either built content or children to show. */
		isMegaItem: function (item) {
			if (!item || !(item.type === 'mega' || item.megaLayout || (item.megaContent && item.megaContent.layout))) return false;
			return !!this.megaOf(item) || this.hasChildren(item);
		},

		/** Normalised built mega content (columns of blocks), or null for the legacy children-as-columns layout. Cached per item. */
		megaOf: function (item) {
			var mc = item && item.megaContent;
			if (!mc || !(Array.isArray(mc.columns) || Array.isArray(mc.slots))) return null;
			if (!this._megaCache) this._megaCache = {};
			var key = item.id + ':' + JSON.stringify(mc);
			if (!this._megaCache[key]) {
				this._megaCache = {};
				this._megaCache[key] = normaliseMega(mc.layout || item.megaLayout || '3col', mc);
			}
			return this._megaCache[key];
		},
		megaGrid: function (item) { return gridTemplate(this.megaOf(item)); },
		bgImage: bgImage,
		megaLinksFor: function (item, ci, bi) {
			var mega = this.megaOf(item);
			if (!mega) return [];
			var key = item.id + ':links:' + (item.children || []).length;
			if (!this._megaLinks || this._megaLinks.key !== key || this._megaLinks.mega !== mega) {
				this._megaLinks = { key: key, mega: mega, map: assignLinks(mega, item.children || []) };
			}
			return this._megaLinks.map[ci + ':' + bi] || [];
		},

		hoverEnter: function (id) {
			clearTimeout(this._hoverCloseTimer);
			this.openItemId = id;
		},

		/** Short grace period so the pointer can cross the gap into the menu. */
		hoverLeave: function () {
			var self = this;
			clearTimeout(this._hoverCloseTimer);
			this._hoverCloseTimer = setTimeout(function () { self.openItemId = null; }, 160);
		},

		onLinkClick: function (item, e) {
			if (this.opensPanel(item) && (!item.url || item.url === '#')) {
				e.preventDefault();
				this.openItemId = this.openItemId === item.id ? null : item.id;
			}
		},

		toggleMobile: function () {
			this.mobileOpen = !this.mobileOpen;
			document.body.style.overflow = this.mobileOpen ? 'hidden' : '';
		},

		closeMobile: function () {
			this.mobileOpen = false;
			document.body.style.overflow = '';
		},

		openSearch: function () {
			var self = this;
			this.closeMobile();
			this.searchOpen = true;
			this.$nextTick(function () { if (self.$refs.searchInput) self.$refs.searchInput.focus(); });
		},

		closeSearch: function () {
			this.searchOpen = false;
		},

		submitSearch: function () {
			var q = (this.searchQuery || '').trim();
			if (!q) return;
			var base = this.searchUrl || '/search';
			window.location.href = base + (base.indexOf('?') === -1 ? '?' : '&') + 'q=' + encodeURIComponent(q);
		},

		toggleMobileItem: function (id) {
			this.$set(this.mobileExpanded, id, !this.mobileExpanded[id]);
		},

		flattenChildren: function (item, depth) {
			depth = depth || 1;
			var out = [];
			if (!this.hasChildren(item)) { return out; }
			for (var i = 0; i < item.children.length; i++) {
				var c = item.children[i];
				var copy = Object.assign({}, c, { _depth: depth });
				out.push(copy);
				if (this.hasChildren(c)) {
					out = out.concat(this.flattenChildren(c, depth + 1));
				}
			}
			return out;
		},

		// ── Per-item dynamic style helpers ──

		computedLinkStyle: function (item) {
			var style = Object.assign({}, this.linkBaseStyle);
			if (this.isActiveItem(item)) {
				if (this.activeColor) style.color = this.activeColor;
				if (this.activeBg) style.backgroundColor = this.activeBg;
			}
			return style;
		},

		badgeStyle: function (item) {
			return {
				backgroundColor: (item && item.badgeColor) || this.badgeBg || 'var(--site-accent-color, #FB6A00)',
				color: this.badgeColor,
				borderRadius: this.badgeRadius,
				fontSize: this.badgeFontSize
			};
		},

		mobileChildLinkStyle: function (child) {
			return {
				paddingLeft: (parseInt(this.mobileChildIndent, 10) + ((child._depth || 1) * 14)) + 'px',
				paddingRight: '14px',
				paddingTop: '0.6rem',
				paddingBottom: '0.6rem',
				color: this.mobileChildColor || 'rgba(30,32,49,0.75)',
				fontSize: '0.95em'
			};
		}
	}
};
</script>

<style scoped>
.cms-navigation-block {
	position: relative;
	width: 100%;
}
/* An open menu must paint above everything that follows the header */
.cms-nav--menu-open { z-index: 200; }

.cms-nav--sticky {
	position: sticky;
	top: 0;
	z-index: 100;
}
/* Thin bar: the bar owns colors and height; the menu only lays out its links. */
.cms-nav--condensed { background: transparent !important; padding: 0 !important; font-size: .92em; }
.cms-nav--condensed .cms-nav__desktop { min-height: 0; }
.cms-nav--condensed .cms-nav__cta { padding: .3rem .8rem; font-size: .9em; }
/* one line only: the thin bar must stay thin, so the root list never wraps */
.cms-nav--condensed .cms-nav__list--root { flex-wrap: nowrap; gap: .1rem !important; overflow: hidden; }
.cms-nav--condensed .cms-nav__list--root > li { flex: 0 0 auto; }

/* ── Desktop ─────────────────────────────────────────── */
.cms-nav__desktop {
	display: flex;
	align-items: center;
	width: 100%;
	position: relative;
	z-index: 1;
}

.cms-nav__desktop--v-center  { align-items: center; }
.cms-nav__desktop--v-start   { align-items: flex-start; }
.cms-nav__desktop--v-end     { align-items: flex-end; }
.cms-nav__desktop--v-stretch { align-items: stretch; }

.cms-nav__list--root {
	display: flex;
	flex-wrap: wrap;
	list-style: none;
	margin: 0;
	padding: 0;
	flex: 1;
}

.cms-nav__desktop--center .cms-nav__list--root        { justify-content: center; }
.cms-nav__desktop--right  .cms-nav__list--root        { justify-content: flex-end; }
.cms-nav__desktop--left   .cms-nav__list--root        { justify-content: flex-start; }
.cms-nav__desktop--space-between .cms-nav__list--root { justify-content: space-between; }

/* Vertical layout (footers, sidebars): one item per line, left aligned,
   row gap only; horizontal item padding is dropped so text lines up. */
.cms-nav--vertical .cms-nav__desktop { align-items: flex-start; flex-direction: column; }
.cms-nav--vertical .cms-nav__list--root {
	flex-direction: column;
	flex-wrap: nowrap;
	align-items: flex-start;
	width: 100%;
	gap: 0.25rem !important;
}
.cms-nav--vertical .cms-nav__item { width: 100%; }
.cms-nav--vertical .cms-nav__link { padding-left: 0 !important; padding-right: 0 !important; }
.cms-nav--vertical .cms-nav__list--submenu { position: static; box-shadow: none; padding-left: 1rem; }

.cms-nav__item {
	position: relative;
	display: flex;
	align-items: center;
}

.cms-nav__link {
	display: inline-flex;
	align-items: center;
	gap: 0.4rem;
	text-decoration: none;
	color: inherit;
	white-space: nowrap;
	position: relative;
}

.cms-nav__link:hover {
	color: var(--cms-nav-hover-color, inherit);
	background-color: var(--cms-nav-hover-bg, transparent);
}

.cms-nav__link--active {
	color: var(--cms-nav-active-color, inherit);
	background-color: var(--cms-nav-active-bg, transparent);
}

/* Active indicators */
.cms-nav--indicator-underline .cms-nav__link--active::after {
	content: '';
	position: absolute;
	bottom: 0;
	left: 0;
	right: 0;
	height: 2px;
	background: var(--cms-nav-active-color, currentColor);
	border-radius: 1px;
}

.cms-nav--indicator-dot .cms-nav__link--active::after {
	content: '';
	position: absolute;
	bottom: -4px;
	left: 50%;
	transform: translateX(-50%);
	width: 5px;
	height: 5px;
	border-radius: 50%;
	background: var(--cms-nav-active-color, currentColor);
}

.cms-nav__icon { font-size: 0.95em; }

.cms-nav__caret {
	opacity: 0.6;
	transition: transform 0.2s;
}

.cms-nav__item--has-children:hover .cms-nav__caret {
	transform: rotate(180deg);
}

.cms-nav__badge {
	display: inline-block;
	padding: 1px 6px;
	font-weight: 600;
	margin-left: 0.4rem;
	line-height: 1.4;
}

/* ── Desktop Logo ────────────────────────────────────── */
.cms-nav__desktop-logo {
	display: inline-flex;
	align-items: center;
	text-decoration: none;
	color: inherit;
	flex-shrink: 0;
}

.cms-nav__desktop-logo img {
	display: block;
}

/* ── CTA Button ──────────────────────────────────────── */
.cms-nav__search-btn {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	background: transparent;
	border: 0;
	cursor: pointer;
	font: inherit;
	/* A button does not inherit colour by default. Without this the icon stays black on a dark bar while the links follow the bar's text colour. */
	color: inherit;
	flex-shrink: 0;
	margin-left: auto;
}
.cms-nav__search-btn + .cms-nav__cta { margin-left: 0.5rem; }
.cms-nav__mobile-actions { display: flex; align-items: center; gap: 0.25rem; }

/* Search modal */
.cms-nav__search-modal {
	position: fixed;
	inset: 0;
	z-index: 1200;
	background: rgba(17, 17, 17, 0.6);
	display: flex;
	align-items: flex-start;
	justify-content: center;
	padding: 12vh 1rem 1rem;
}
.cms-nav__search-form {
	position: relative;
	display: flex;
	align-items: center;
	gap: 0.5rem;
	width: 100%;
	max-width: 640px;
	background: var(--site-page-color, #FFFFFF);
	color: var(--site-body-color, #1E2031);
	border-radius: 10px;
	padding: 0.75rem 0.75rem 0.75rem 1rem;
	box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
}
.cms-nav__search-icon { opacity: 0.5; }
.cms-nav__search-input {
	flex: 1;
	min-width: 0;
	border: 0;
	outline: 0;
	background: transparent;
	font: inherit;
	font-size: 1.15em;
	color: inherit;
}
.cms-nav__search-submit {
	border: 0;
	cursor: pointer;
	font: inherit;
	white-space: nowrap;
}
.cms-nav__search-close {
	background: transparent;
	border: 0;
	cursor: pointer;
	opacity: 0.6;
	font-size: 1.1em;
	padding: 0.25rem;
}
.cms-nav__search-close:hover { opacity: 1; }

.cms-nav__cta {
	display: inline-flex;
	align-items: center;
	text-decoration: none;
	white-space: nowrap;
	flex-shrink: 0;
	transition: background 0.15s, color 0.15s;
}

.cms-nav__cta:hover {
	background-color: var(--cms-nav-cta-hover-bg, inherit) !important;
	color: var(--cms-nav-cta-hover-color, inherit) !important;
}

/* ── Standard dropdown ───────────────────────────────── */
.cms-nav__list--dropdown {
	position: absolute;
	top: 100%;
	left: 0;
	list-style: none;
	margin: 0;
	z-index: 50;
	animation: cmsNavDropIn 0.15s ease;
}
/* right-aligned navs open their menus toward the left so they never leave the viewport */
.cms-nav--align-right .cms-nav__item--has-children:not(.cms-nav__item--child) > .cms-nav__list--dropdown { left: auto; right: 0; }
.cms-nav--align-right .cms-nav__list--submenu { left: auto; right: calc(100% + 4px); }
.cms-nav--align-center .cms-nav__item--has-children:not(.cms-nav__item--child) > .cms-nav__list--dropdown { left: 50%; transform: translateX(-50%); }
/* invisible bridge over the offset gap so hovering down into the menu never leaves the item */
.cms-nav__list--dropdown::before,
.cms-nav__mega::before {
	content: '';
	position: absolute;
	left: 0;
	right: 0;
	bottom: 100%;
	height: var(--nav-drop-offset, 8px);
}

@keyframes cmsNavDropIn {
	from { opacity: 0; margin-top: calc(var(--nav-drop-offset, 4px) - 6px); }
	to   { opacity: 1; }
}

.cms-nav__link--child {
	width: 100%;
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 0.5rem;
	text-decoration: none;
	color: inherit;
	border-radius: 6px;
	line-height: 1.3;
	transition: background 0.1s;
}
.cms-nav__item--child { position: relative; }
.cms-nav__caret--child { font-size: .7em; opacity: .6; }

.cms-nav__link--child:hover {
	background: var(--cms-nav-dd-hover-bg, rgba(0,0,0,0.04));
}

.cms-nav__list--submenu {
	display: none;
	position: absolute;
	top: -0.4rem;
	left: calc(100% + 4px);
	min-width: 200px;
	list-style: none;
	padding: 0.5rem 0;
	margin: 0;
	z-index: 51;
	animation: cmsNavDropIn 0.15s ease;
}

.cms-nav__item--child:hover > .cms-nav__list--submenu {
	display: block;
}

/* ── Mega menu: slot columns ─────────────────────────── */
.cms-nav__mega-grid--built { display: grid; gap: var(--cms-mega-gap, 2rem); }
.cms-nav__mega-col { display: flex; flex-direction: column; gap: 1rem; min-width: 0; }
.cms-nav__mega-block { min-width: 0; }
.cms-nav__mega-image { display: flex; flex-direction: column; gap: .4rem; text-decoration: none; color: inherit; }
.cms-nav__mega-text :deep(p) { margin: 0 0 .4em; }
.cms-nav__mega-divider { margin: 0; border: 0; border-top: 1px solid rgba(0,0,0,.08); }
.cms-nav__mega-promo-btn--outline { background: transparent; color: var(--site-btn-primary-bg, var(--site-accent-color, #FB6A00)); border: 1px solid currentColor; }
.cms-nav__mega-posts { display: flex; flex-direction: column; gap: .75rem; }
.cms-nav__mega-posts--cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: .9rem; }
.cms-nav__mega-post { display: flex; flex-direction: column; gap: .4rem; text-decoration: none; color: inherit; min-width: 0; }
.cms-nav__mega-post:hover .cms-nav__mega-post-title { text-decoration: underline; }
.cms-nav__mega-post-img { display: block; aspect-ratio: 16 / 10; border-radius: var(--site-image-radius, 6px); background: #E9E9EA center / cover no-repeat; }
.cms-nav__mega-post-title { font-weight: 600; font-size: .95em; line-height: 1.25; }
.cms-nav__mega-posts--list .cms-nav__mega-post { padding: .25rem 0; border-bottom: 1px solid rgba(0,0,0,.06); }
.cms-nav__mega-posts--list .cms-nav__mega-post:last-child { border-bottom: 0; }
.cms-nav__mega-promo { display: flex; flex-direction: column; gap: .6rem; }
.cms-nav__mega-promo-img { display: block; aspect-ratio: 4 / 3; border-radius: var(--site-image-radius, 6px); background: #E9E9EA center / cover no-repeat; }
.cms-nav__mega-promo-btn { align-self: flex-start; padding: .5rem 1rem; border-radius: var(--site-btn-radius, 4px); background: var(--site-btn-primary-bg, var(--site-accent-color, #FB6A00)); color: var(--site-btn-primary-color, #FFF); font-weight: 600; text-decoration: none; font-size: .9em; }

/* ── Mega menu ───────────────────────────────────────── */
.cms-nav__mega {
	position: absolute;
	top: 100%;
	left: 0;
	z-index: 50;
	box-sizing: border-box;
	max-width: 100%;
	background: var(--cms-nav-dd-bg, #FFF);
	animation: cmsNavDropIn 0.2s ease;
}
/* "Page width": the panel is centred under the bar and never wider than the page */
.cms-nav__mega--page { left: 50%; transform: translateX(-50%); }
/* A mega panel spans the bar, not the item, so the item must not be its
   positioning parent. The bar (.cms-nav__desktop) is position: relative. */
.cms-nav__item--mega { position: static; }
.cms-nav--vertical .cms-nav__item--mega { position: relative; }

.cms-nav__mega-grid {
	display: grid;
	gap: var(--cms-mega-gap, 1.5rem);
}

.cms-nav__mega--2col .cms-nav__mega-grid { grid-template-columns: repeat(2, 1fr); }
.cms-nav__mega--3col .cms-nav__mega-grid { grid-template-columns: repeat(3, 1fr); }
.cms-nav__mega--4col .cms-nav__mega-grid { grid-template-columns: repeat(4, 1fr); }
.cms-nav__mega--5col .cms-nav__mega-grid { grid-template-columns: repeat(5, 1fr); }
.cms-nav__mega--featured .cms-nav__mega-grid { grid-template-columns: 2fr 1fr; }

.cms-nav__mega-heading {
	margin-bottom: 0.4rem;
	color: inherit;
}

.cms-nav__mega-desc {
	font-size: 0.85em;
	margin-bottom: 0.5rem;
}

.cms-nav__mega-list {
	list-style: none;
	padding: 0;
	margin: 0;
	display: flex;
	flex-direction: column;
	gap: 0.25rem;
}

.cms-nav__mega-link {
	display: flex;
	flex-direction: column;
	text-decoration: none;
	color: inherit;
	transition: background 0.1s;
}

.cms-nav__mega-link:hover {
	background: var(--cms-mega-link-hover-bg, rgba(0,0,0,0.04));
}

.cms-nav__mega-link-desc {
	font-size: 0.8em;
	opacity: 0.6;
}

/* ── Mobile bar ──────────────────────────────────────── */
.cms-nav__mobile-bar {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	display: flex;
	align-items: center;
	justify-content: space-between;
	z-index: 1000;
}
.cms-nav__mobile-bar--inline {
	position: static;
	z-index: auto;
}

.cms-nav__mobile-logo {
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	text-decoration: none;
	color: inherit;
	font-weight: 700;
}

.cms-nav__mobile-logo img {
	display: block;
}

.cms-nav__mobile-toggle {
	background: transparent;
	border: none;
	color: inherit;
	font-size: 1.4em;
	cursor: pointer;
	padding: 4px 8px;
	border-radius: 4px;
	transition: background 0.15s;
}

.cms-nav__mobile-toggle:hover {
	background: rgba(0,0,0,0.05);
}

/* ── Mobile overlay drawer ───────────────────────────── */
.cms-nav__overlay {
	position: fixed;
	inset: 0;
	z-index: 999;
	display: flex;
	flex-direction: column;
}

.cms-nav__overlay-panel {
	height: 100%;
	overflow-y: auto;
	display: flex;
	flex-direction: column;
}

.cms-nav__overlay-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 0 14px;
	height: 56px;
	flex-shrink: 0;
}

.cms-nav__list--mobile {
	list-style: none;
	margin: 0;
	padding: 0.5rem 0;
	flex: 1;
}

.cms-nav__mobile-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.cms-nav__mobile-link {
	flex: 1;
	display: inline-flex;
	align-items: center;
	gap: 0.6rem;
	text-decoration: none;
	color: inherit;
	transition: background 0.1s;
}

.cms-nav__mobile-link:hover {
	background: var(--cms-nav-mobile-hover-bg, rgba(0,0,0,0.04));
}

.cms-nav__mobile-link--child {
	font-size: 0.95em;
}

.cms-nav__mobile-expand {
	background: transparent;
	border: none;
	color: inherit;
	font-size: 1em;
	cursor: pointer;
	padding: 0 14px;
	height: 100%;
	transition: transform 0.2s;
}

.cms-nav__list--mobile-children {
	list-style: none;
	margin: 0;
	padding: 0;
}

/* ── Mobile CTA ──────────────────────────────────────── */
.cms-nav__mobile-cta-wrap {
	padding: 1rem 14px;
	flex-shrink: 0;
}

.cms-nav__mobile-cta {
	display: block;
	text-align: center;
	text-decoration: none;
	width: 100%;
	transition: opacity 0.15s;
}

.cms-nav__mobile-cta:hover {
	opacity: 0.9;
}

/* ── Transitions ─────────────────────────────────────── */
.cms-nav-overlay-fade-enter-active,
.cms-nav-overlay-fade-leave-active {
	transition: opacity 0.2s ease;
}
.cms-nav-overlay-fade-enter,
.cms-nav-overlay-fade-leave-to {
	opacity: 0;
}

/* ── Empty state ─────────────────────────────────────── */
.cms-nav__empty {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 0.5rem;
	padding: 1rem;
	min-height: 50px;
	color: #999;
	font-size: 0.95em;
	background: #F4F4F5;
	border: 2px dashed #DDDEE0;
	border-radius: 4px;
}

.cms-nav__empty .fa { font-size: 1.2em; }
</style>

